import { createDebugLogger } from "@evidenceb/debug-utils";
import { createRxDatabase, RxError } from "rxdb";
import { migrateStorage } from "rxdb/plugins/migration-storage";
import { getRxStorageDexie } from "rxdb/plugins/storage-dexie";
import { getRxStorageMemory } from "rxdb/plugins/storage-memory";
import { wrappedValidateZSchemaStorage } from "rxdb/plugins/validate-z-schema";
import { LAST_SYNC_INFO_LOCAL_DOC_ID, } from "../AthenaEventStream/LastSyncInfo.js";
import { MIGRATION_CUTOFF_LOCAL_DOC_ID, } from "../AthenaEventStream/MigrationCutoff.js";
import { isPreprodOrLocal, isTest } from "./envCheck.js";
const debug = createDebugLogger("athena-event-stream");
async function loadRxStorageDexieV15() {
    const module = await import("rxdb-old/plugins/storage-dexie");
    return module.getRxStorageDexie;
}
function deleteIndexedDbDatabase(name) {
    return new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(name);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
        request.onblocked = () => resolve();
    });
}
/**
 * Some old firefox versions don't support indexedDB.databases
 */
function supportsIndexedDbEnumeration() {
    return (typeof indexedDB !== "undefined" &&
        typeof indexedDB.databases === "function");
}
function enumerateIndexedDbNames(prefix) {
    return indexedDB
        .databases()
        .then((dbs) => dbs
        .map((db) => db.name)
        .filter((name) => !!name && name.startsWith(prefix)));
}
async function removeOldDexieDatabases(databaseName) {
    const names = await enumerateIndexedDbNames(`rxdb-dexie-${databaseName}--`);
    await Promise.all(names.map((name) => deleteIndexedDbDatabase(name)));
    return names;
}
function dexieDatabaseExists(databaseName) {
    return enumerateIndexedDbNames(`rxdb-dexie-${databaseName}--`).then((names) => names.length > 0);
}
const toBytes = async (input) => {
    if (typeof Blob !== "undefined" && input instanceof Blob) {
        return new Uint8Array(await input.arrayBuffer());
    }
    if (typeof input === "string") {
        return new TextEncoder().encode(input);
    }
    return new Uint8Array(input);
};
const fallbackHash = async (input) => {
    const bytes = await toBytes(input);
    let hash = 2166136261;
    for (const byte of bytes) {
        hash ^= byte;
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
};
export const hashForRxdb = async (input) => {
    if (typeof crypto !== "undefined" &&
        typeof crypto.subtle?.digest === "function") {
        const bytes = await toBytes(input);
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    }
    return fallbackHash(input);
};
export function pickStorage() {
    if (isTest()) {
        return wrappedValidateZSchemaStorage({ storage: getRxStorageMemory() });
    }
    if (isPreprodOrLocal()) {
        return wrappedValidateZSchemaStorage({ storage: getRxStorageDexie() });
    }
    return getRxStorageDexie();
}
export async function createDefaultDatabaseInstance(databaseName, collections, eventReduce = true, localDocuments = true) {
    const rxdb = await createRxDatabase({
        name: databaseName,
        storage: pickStorage(),
        hashFunction: hashForRxdb,
        eventReduce,
        localDocuments,
    });
    await rxdb.addCollections(collections);
    return rxdb;
}
export function migratedDatabaseName(databaseName) {
    return `${databaseName}_rxdb17`;
}
export function isDm5Error(error) {
    return error instanceof RxError && error.code === "DM5";
}
/**
 * Reads `lastSyncedAt` from the pre-migration database's `LastSyncInfo`
 * local document, using the matching old RxDB major version (`rxdb-old`) so
 * that opening it doesn't itself trigger a DM5 mismatch. Local documents
 * live in their own per-collection storage instance, separate from the
 * collection's regular documents, so `migrateStorage` never touches them and
 * this is the only way to recover the value pre-migration.
 *
 * Returns `undefined` (rather than throwing) on any failure, so the caller
 * can fall back to a coarser cutoff instead of failing the migration.
 */
async function readOldLastSyncedAt({ oldDatabaseName, collectionName, schema, maskedUserId, }) {
    let oldDb;
    try {
        const [{ createRxDatabase: createRxDatabaseOld, addRxPlugin: addRxPluginOld }, { RxDBLocalDocumentsPlugin: RxDBLocalDocumentsPluginOld }, { getRxStorageDexie: getRxStorageDexieOld },] = await Promise.all([
            import("rxdb-old"),
            import("rxdb-old/plugins/local-documents"),
            import("rxdb-old/plugins/storage-dexie"),
        ]);
        addRxPluginOld(RxDBLocalDocumentsPluginOld);
        oldDb = await createRxDatabaseOld({
            name: oldDatabaseName,
            storage: getRxStorageDexieOld(),
        });
        await oldDb.addCollections({
            [collectionName]: { schema, localDocuments: true },
        });
        const oldCollection = oldDb[collectionName];
        const lastSyncDoc = await oldCollection.getLocal(LAST_SYNC_INFO_LOCAL_DOC_ID);
        return lastSyncDoc?.get("lastSyncedAt");
    }
    catch (error) {
        console.warn("[athena-event-stream] failed to read pre-migration lastSyncedAt", { userId: maskedUserId, oldDatabaseName, error });
        return undefined;
    }
    finally {
        // rxdb-old (v15) uses `destroy()`, not `close()`, to close a database
        // without removing its persisted data.
        await oldDb?.destroy().catch(() => { });
    }
}
/**
 * Determines the `MigrationCutoff` to record for the migrated database, so
 * that `AthenaEventStream` can skip re-pushing AthenaEvents that already
 * existed before migration (see `MigrationCutoff.ts` for why this is
 * needed).
 */
async function determineMigrationCutoff({ migratedDb, collectionName, oldDatabaseName, schema, maskedUserId, }) {
    const oldLastSyncedAt = await readOldLastSyncedAt({
        oldDatabaseName,
        collectionName,
        schema,
        maskedUserId,
    });
    if (oldLastSyncedAt) {
        return { createdAt: new Date(oldLastSyncedAt).toISOString() };
    }
    const [mostRecentlyCreated] = await migratedDb[collectionName]
        .find({ selector: {}, sort: [{ createdAt: "desc" }], limit: 1 })
        .exec();
    if (!mostRecentlyCreated) {
        return null;
    }
    return { createdAt: mostRecentlyCreated.get("createdAt") };
}
export async function createDatabaseWithDm5Migration({ databaseName, collections, maskedUserId, }) {
    const migratedName = migratedDatabaseName(databaseName);
    if (!supportsIndexedDbEnumeration()) {
        // If we can't enumerate databases, we can't tell if the migrated database
        // exists. So we just skip migration. Risk is fine : we'll lose offline events of users specifically using old versions of Firefox
        debug.info("indexedDB.databases() unsupported, opening migrated database name directly", { userId: maskedUserId, migratedDatabaseName: migratedName });
        return await createDefaultDatabaseInstance(migratedName, collections);
    }
    const [migratedExists, oldExists] = await Promise.all([
        dexieDatabaseExists(migratedName),
        dexieDatabaseExists(databaseName),
    ]);
    if (migratedExists || !oldExists) {
        debug.info("using existing migrated database", {
            userId: maskedUserId,
            migratedDatabaseName: migratedName,
        });
        return await createDefaultDatabaseInstance(migratedName, collections);
    }
    try {
        return await createDefaultDatabaseInstance(databaseName, collections);
    }
    catch (error) {
        if (!isDm5Error(error) || isTest()) {
            throw error;
        }
        console.warn("[athena-event-stream] DM5 detected, starting migration", {
            userId: maskedUserId,
            databaseName,
            migratedDatabaseName: migratedName,
        });
        const migratedDb = await createDefaultDatabaseInstance(migratedName, collections);
        debug.info("storage migration begin", {
            userId: maskedUserId,
            oldDatabaseName: databaseName,
            migratedDatabaseName: migratedName,
        });
        const oldStorage = (await loadRxStorageDexieV15())();
        await migrateStorage({
            database: migratedDb,
            oldDatabaseName: databaseName,
            oldStorage,
            batchSize: 500,
            parallel: false,
            afterMigrateBatch: () => {
                debug.info("storage migration batch done", {
                    userId: maskedUserId,
                    oldDatabaseName: databaseName,
                    migratedDatabaseName: migratedName,
                });
            },
        });
        const cutoffCollectionEntry = Object.entries(collections).find(([, config]) => config.localDocuments);
        if (cutoffCollectionEntry) {
            const [cutoffCollectionName, cutoffCollectionConfig] = cutoffCollectionEntry;
            try {
                const migrationCutoff = await determineMigrationCutoff({
                    migratedDb: migratedDb,
                    collectionName: cutoffCollectionName,
                    oldDatabaseName: databaseName,
                    schema: cutoffCollectionConfig.schema,
                    maskedUserId,
                });
                if (migrationCutoff) {
                    const newCollection = migratedDb[cutoffCollectionName];
                    await newCollection.insertLocal(MIGRATION_CUTOFF_LOCAL_DOC_ID, migrationCutoff);
                    debug.info("migration cutoff recorded", {
                        userId: maskedUserId,
                        migrationCutoff,
                    });
                }
            }
            catch (error) {
                console.warn("[athena-event-stream] failed to record migration cutoff", { userId: maskedUserId, error });
            }
        }
        // Remove old Dexie IndexedDB databases after successful migration.
        // We avoid oldDb.remove() here because it executes queries that can fail
        // on mingo operator registration mismatches in mixed-version bundles.
        try {
            const removedDbNames = await removeOldDexieDatabases(databaseName);
            debug.info("old database removed after migration", {
                userId: maskedUserId,
                removedDatabaseName: databaseName,
                removedDexieDatabases: removedDbNames,
            });
        }
        catch (removeError) {
            console.warn("[athena-event-stream] failed to remove old database after migration", {
                userId: maskedUserId,
                oldDatabaseName: databaseName,
                error: removeError,
            });
        }
        debug.info("storage migration done", {
            userId: maskedUserId,
            oldDatabaseName: databaseName,
            migratedDatabaseName: migratedName,
        });
        return migratedDb;
    }
}
//# sourceMappingURL=dbMigration.js.map